import { useState } from 'react';
import type { FunctionComponent } from 'react';
import DataTable from '@components/DataTable';
import { Toggle } from '@simplywallst/ui-core';
import { ToggleCell } from './components/ToggleCell';
import track, { useTracking } from 'react-tracking';
import { CellLabel, RowHeading, ToggleCellLabel } from './styled';
import { useOverviewTranslation } from '@/pages/CompanyReport/components/OverviewSection/hooks/useOverviewTranslation';

interface Props {
  uniqueSymbol: string;
  company7D?: string | number | null;
  company30D?: string | number | null;
  company90D?: string | number | null;
  company1YrAbs?: string | number | null;
  company1YrTotal?: string | number | null;
  company3YrAbs?: string | number | null;
  company3YrTotal?: string | number | null;
  company5YrAbs?: string | number | null;
  company5YrTotal?: string | number | null;
  industry7D?: string | number | null;
  industry30D?: string | number | null;
  industry90D?: string | number | null;
  industry1YrAbs?: string | number | null;
  industry1YrTotal?: string | number | null;
  industry3YrAbs?: string | number | null;
  industry3YrTotal?: string | number | null;
  industry5YrAbs?: string | number | null;
  industry5YrTotal?: string | number | null;
  market7D?: string | number | null;
  market30D?: string | number | null;
  market90D?: string | number | null;
  market1YrAbs?: string | number | null;
  market1YrTotal?: string | number | null;
  market3YrAbs?: string | number | null;
  market3YrTotal?: string | number | null;
  market5YrAbs?: string | number | null;
  market5YrTotal?: string | number | null;
}

const ShareHolderReturns: FunctionComponent<Props> = ({
  uniqueSymbol,
  company7D,
  company30D,
  company90D,
  company1YrAbs,
  company1YrTotal,
  company3YrAbs,
  company3YrTotal,
  company5YrAbs,
  company5YrTotal,
  industry7D,
  industry30D,
  industry90D,
  industry1YrAbs,
  industry1YrTotal,
  industry3YrAbs,
  industry3YrTotal,
  industry5YrAbs,
  industry5YrTotal,
  market7D,
  market30D,
  market90D,
  market1YrAbs,
  market1YrTotal,
  market3YrAbs,
  market3YrTotal,
  market5YrAbs,
  market5YrTotal,
}) => {
  const [toggleDividendReturns, setToggleDividendReturns] = useState(false);
  const { trackEvent } = useTracking();
  const t = useOverviewTranslation();

  return (
    <>
      <DataTable
        light
        columnWidths="3, 2, 2, 2"
        columnTextAlignments="l, r, r, r"
      >
        <thead>
          <tr>
            <th />
            <RowHeading>{uniqueSymbol}</RowHeading>
            <th>{t('pricePerformance.shareReturns.table.industry')}</th>
            <th>
              {t('pricePerformance.shareReturns.table.market', {
                exchangeCountryISO: '',
              })}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <CellLabel>
              {t('pricePerformance.shareReturns.table.7Day')}
            </CellLabel>
            <td>{company7D}</td>
            <td>{industry7D}</td>
            <td>{market7D}</td>
          </tr>
          <tr>
            <CellLabel>
              {t('pricePerformance.shareReturns.table.30Day')}
            </CellLabel>
            <td>{company30D}</td>
            <td>{industry30D}</td>
            <td>{market30D}</td>
          </tr>
          <tr>
            <CellLabel>
              {t('pricePerformance.shareReturns.table.90Day')}
            </CellLabel>
            <td>{company90D}</td>
            <td>{industry90D}</td>
            <td>{market90D}</td>
          </tr>
          <tr>
            <CellLabel>
              {t('pricePerformance.shareReturns.table.1Year')}
            </CellLabel>
            <ToggleCell
              toggled={toggleDividendReturns}
              totalValue={company1YrTotal}
              absValue={company1YrAbs}
            />
            <ToggleCell
              toggled={toggleDividendReturns}
              totalValue={industry1YrTotal}
              absValue={industry1YrAbs}
            />
            <ToggleCell
              toggled={toggleDividendReturns}
              totalValue={market1YrTotal}
              absValue={market1YrAbs}
            />
          </tr>
          <tr>
            <CellLabel>
              {t('pricePerformance.shareReturns.table.3Year')}
            </CellLabel>
            <ToggleCell
              toggled={toggleDividendReturns}
              totalValue={company3YrTotal}
              absValue={company3YrAbs}
            />
            <ToggleCell
              toggled={toggleDividendReturns}
              totalValue={industry3YrTotal}
              absValue={industry3YrAbs}
            />
            <ToggleCell
              toggled={toggleDividendReturns}
              totalValue={market3YrTotal}
              absValue={market3YrAbs}
            />
          </tr>
          <tr>
            <CellLabel>
              {t('pricePerformance.shareReturns.table.5Year')}
            </CellLabel>
            <ToggleCell
              toggled={toggleDividendReturns}
              totalValue={company5YrTotal}
              absValue={company5YrAbs}
            />
            <ToggleCell
              toggled={toggleDividendReturns}
              totalValue={industry5YrTotal}
              absValue={industry5YrAbs}
            />
            <ToggleCell
              toggled={toggleDividendReturns}
              totalValue={market5YrTotal}
              absValue={market5YrAbs}
            />
          </tr>
          <tr>
            <td colSpan={4}>
              <Toggle
                name="include_dividend_returns"
                value={toggleDividendReturns}
                label={
                  <ToggleCellLabel>
                    {t('pricePerformance.shareReturns.table.includeDividend')}
                  </ToggleCellLabel>
                }
                onChange={(e) => {
                  setToggleDividendReturns((prevToggle) => !prevToggle);
                  trackEvent({
                    action: 'toggle',
                    modifier: 'includeDividendReturns',
                    active: e.target.checked,
                  });
                }}
              />
            </td>
          </tr>
        </tbody>
      </DataTable>
    </>
  );
};

ShareHolderReturns.defaultProps = {
  company7D: 'n/a',
  company30D: 'n/a',
  company90D: 'n/a',
  company1YrAbs: 'n/a',
  company3YrAbs: 'n/a',
  company5YrAbs: 'n/a',
  industry7D: 'n/a',
  industry30D: 'n/a',
  industry90D: 'n/a',
  industry1YrAbs: 'n/a',
  industry3YrAbs: 'n/a',
  industry5YrAbs: 'n/a',
  market7D: 'n/a',
  market30D: 'n/a',
  market90D: 'n/a',
  market1YrAbs: 'n/a',
  market3YrAbs: 'n/a',
  market5YrAbs: 'n/a',
};

export default track({
  type: 'track',
  subject: 'shareholder_returns',
})(ShareHolderReturns) as typeof ShareHolderReturns;
